/**
 * ============================================================================
 * EquipmentWorkbench — Phase 6e frontend (Equipment & Validation Repository).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /equipment
 * Purpose: top-level Engineering + Validation workbench. Four hero KPI tiles
 *          headline the page (Total Qualified / Restricted Count / Cal Due
 *          This Week / Requal Due This Month), a four-axis filter bar below,
 *          then the equipment table. "Add Equipment" opens a dialog that
 *          creates PLANNED state with category defaults auto-filled.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Empty state has a primary CTA + governance copy.
 *   - Loading: skeleton rows (not a spinner-only).
 *   - Error: refined envelope with Retry button.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './EquipmentWorkbench.module.css';
import { WorkbenchStats } from './WorkbenchStats';
import { WorkbenchFilters } from './WorkbenchFilters';
import { EquipmentRow } from './EquipmentRow';
import { AddEquipmentDialog } from './AddEquipmentDialog';
import {
  useEquipmentQuery,
  type EquipmentQueryParams,
  type EquipmentSummary,
} from './useEquipmentQuery';

export interface EquipmentWorkbenchProps {
  /** Optional callback so the host shell can route to the detail page. */
  onOpenEquipment?: (id: string) => void;
}

export function EquipmentWorkbench({
  onOpenEquipment,
}: EquipmentWorkbenchProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<EquipmentQueryParams>({});
  const [showAdd, setShowAdd] = useState(false);

  const equipmentQ = useEquipmentQuery(filters);
  const nf = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language],
  );

  const items: EquipmentSummary[] = equipmentQ.data?.items ?? [];
  const stats = equipmentQ.data?.stats;
  const isEmpty =
    !equipmentQ.isLoading && !equipmentQ.isError && items.length === 0;

  function handleOpen(e: EquipmentSummary) {
    if (onOpenEquipment) {
      onOpenEquipment(e.equipment_id);
      return;
    }
    navigate({ to: '/equipment/$id', params: { id: e.equipment_id } });
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('equipment.workbench.eyebrow', { defaultValue: 'Equipment' })}
        title={t('equipment.workbench.title', {
          defaultValue: 'Equipment workbench',
        })}
        subtitle={t('equipment.workbench.subtitle', {
          defaultValue:
            'Engineering and Validation view of qualified equipment, calibration posture, and pending requalification work.',
        })}
        actions={
          <Button
            variant="primary"
            leadingIcon="+"
            onClick={() => setShowAdd(true)}
            aria-label={t('equipment.workbench.actions.addAria', {
              defaultValue: 'Add a new equipment record',
            })}
          >
            {t('equipment.workbench.actions.add', {
              defaultValue: 'Add equipment',
            })}
          </Button>
        }
      />

      <WorkbenchStats stats={stats} isLoading={equipmentQ.isLoading} />

      <Card>
        <CardHeader>
          <div className={styles.cardHeaderRow}>
            <h2 className={wstyles.sectionTitle}>
              {t('equipment.workbench.list.title', {
                defaultValue: 'Equipment',
              })}{' '}
              <span className={styles.muted}>
                ({nf.format(items.length)})
              </span>
            </h2>
            <WorkbenchFilters value={filters} onChange={setFilters} />
          </div>
        </CardHeader>
        <CardBody>
          {equipmentQ.isLoading && (
            <div
              role="status"
              aria-live="polite"
              aria-label={t('common.loading', { defaultValue: 'Loading…' })}
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          )}

          {equipmentQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('equipment.workbench.errors.loadFailed', {
                  defaultValue: 'Could not load the equipment list.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => equipmentQ.refetch()}
              >
                {t('equipment.workbench.actions.retry', {
                  defaultValue: 'Retry',
                })}
              </Button>
            </div>
          )}

          {isEmpty && (
            <div className={styles.emptyState}>
              <h3 className={styles.emptyTitle}>
                {t('equipment.workbench.empty.title', {
                  defaultValue: 'No equipment matches your filters.',
                })}
              </h3>
              <p>
                {t('equipment.workbench.empty.body', {
                  defaultValue:
                    'Clear the filters or add a new equipment record to begin qualification.',
                })}
              </p>
              <Button
                variant="primary"
                leadingIcon="+"
                onClick={() => setShowAdd(true)}
              >
                {t('equipment.workbench.actions.add', {
                  defaultValue: 'Add equipment',
                })}
              </Button>
            </div>
          )}

          {!equipmentQ.isLoading && !equipmentQ.isError && items.length > 0 && (
            <table
              className={wstyles.table}
              aria-label={t('equipment.workbench.tableAria', {
                defaultValue: 'Equipment',
              })}
            >
              <caption className={styles.srOnly}>
                {t('equipment.workbench.tableCaption', {
                  defaultValue:
                    'Equipment list with tag, name, manufacturer, criticality + GAMP class, lifecycle status, and next calibration / PM / requalification dates.',
                })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    {t('equipment.workbench.columns.tag', {
                      defaultValue: 'Tag',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.name', {
                      defaultValue: 'Name',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.manufacturer', {
                      defaultValue: 'Manufacturer',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.critGamp', {
                      defaultValue: 'Criticality + GAMP',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.status', {
                      defaultValue: 'Status',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.nextCal', {
                      defaultValue: 'Next cal due',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.nextPm', {
                      defaultValue: 'Next PM due',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.nextRequal', {
                      defaultValue: 'Next requal due',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.location', {
                      defaultValue: 'Location',
                    })}
                  </th>
                  <th scope="col">
                    {t('equipment.workbench.columns.actions', {
                      defaultValue: 'Actions',
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <EquipmentRow
                    key={e.equipment_id}
                    equipment={e}
                    onOpen={handleOpen}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {showAdd && (
        <AddEquipmentDialog
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            equipmentQ.refetch();
          }}
        />
      )}
    </div>
  );
}
